import React from "react";
import ReactDOM from "react-dom/client";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates, arrayMove } from "@dnd-kit/sortable";
import NoResult from "../../../../public/javascripts/reactcomponents/NoResult";
import FormDesignerField from "./FormDesignerField";
import FormDesignerFieldEditor from "./FormDesignerFieldEditor";

/** @typedef {import('../transformers/FormFieldTransformer.js').DesignerFieldObj} DesignerFieldObj */
/** @typedef {import('../transformers/FormFieldTransformer.js').DesignerFormObj} DesignerFormObj */
/** @typedef {import('../enums.js').FormFieldTypeObj} FormFieldTypeObj */

/**
 * @typedef {DesignerFieldObj & { key: string }} DesignerFieldStateObj - Champ du designer avec une clé stable pour le glisser-déposer
 */

/**
 * @typedef {object} FormDesignerProps
 * @property {string} csrf - Jeton CSRF pour les requêtes sécurisées
 * @property {string} formId - Identifiant du formulaire à designer
 */

/**
 * @typedef {object} FormDesignerState
 * @property {string} title - Titre du formulaire
 * @property {Array<DesignerFieldStateObj>} fields - Champs du formulaire dans l'ordre d'affichage
 * @property {Array<FormFieldTypeObj>} types - Types de champ disponibles
 * @property {?string} selectedKey - Clé du champ en cours d'édition
 * @property {boolean} loading - Si le formulaire est en cours de chargement
 * @property {boolean} saving - Si un enregistrement est en cours
 * @property {boolean} dirty - Si des modifications ne sont pas enregistrées
 * @property {string} error - Message d'erreur
 * @property {string} success - Message de succès
 */

// NOTE descripteurs équivalents à useSensor(), les hooks n'étant pas utilisables dans un composant classe
const SENSORS = [
  { sensor: PointerSensor, options: { activationConstraint: { distance: 5 } } },
  { sensor: KeyboardSensor, options: { coordinateGetter: sortableKeyboardCoordinates } }
];

let newFieldCounter = 0;

/**
 * Ajoute une clé stable à un champ pour le glisser-déposer
 * @param {DesignerFieldObj} field - Champ du formulaire
 * @returns {DesignerFieldStateObj}
 */
function withKey(field) {
  newFieldCounter++;

  return { ...field, key: field.id ? `field-${field.id}` : `new-${newFieldCounter}` };
}

class FormDesigner extends React.Component {
  /**
   * @param {FormDesignerProps} props
   */
  constructor(props) {
    super(props);
    this.state = /** @type {FormDesignerState} */ ({
      title: "",
      fields: [],
      types: [],
      selectedKey: null,
      loading: true,
      saving: false,
      dirty: false,
      error: "",
      success: ""
    });
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
    this.loadForm();
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  }

  /**
   * Prévient l'utilisateur s'il quitte la page avec des modifications non enregistrées
   * @param {BeforeUnloadEvent} event - Événement de fermeture de la page
   */
  onBeforeUnload = event => {
    if (this.state.dirty) {
      event.preventDefault();
      event.returnValue = "";
    }
  };

  /**
   * Applique les données du formulaire renvoyées par l'API
   * @param {DesignerFormObj} form - Formulaire transformé pour le designer
   * @param {?number} [selectedPosition] - Position du champ à garder sélectionné après un enregistrement
   */
  applyForm(form, selectedPosition = null) {
    const fields = form.fields.map(withKey);
    const selectedField = selectedPosition ? fields[selectedPosition - 1] : null;

    this.setState({
      title: form.title,
      fields,
      types: form.types,
      selectedKey: selectedField ? selectedField.key : null,
      dirty: false
    });
  }

  /**
   * Charge le formulaire et ses champs
   * @returns {Promise<void>}
   */
  async loadForm() {
    this.setState({ loading: true, error: "" });

    try {
      const response = await fetch(`/api/form/_designer?formId=${parseInt(this.props.formId, 10)}`, { headers: { Authorization: "gestiaweb" } });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Impossible de charger le formulaire.");
      }

      this.applyForm(result.item);
      this.setState({ loading: false });
    } catch (error) {
      this.setState({ loading: false, error: error instanceof Error ? error.message : "Impossible de charger le formulaire." });
    }
  }

  /**
   * Enregistre tous les champs du formulaire
   * @returns {Promise<void>}
   */
  save = async () => {
    const selectedIndex = this.state.fields.findIndex(field => field.key === this.state.selectedKey);

    this.setState({ saving: true, error: "", success: "" });

    try {
      const response = await fetch("/api/form/_designersave", {
        method: "POST",
        headers: {
          Authorization: "gestiaweb",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          formId: parseInt(this.props.formId, 10),
          fields: this.state.fields.map(({ key, ...field }) => field),
          _csrf: this.props.csrf
        })
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Impossible d'enregistrer le formulaire.");
      }

      this.applyForm(result.item, selectedIndex === -1 ? null : selectedIndex + 1);
      this.setState({ saving: false, success: "Formulaire enregistré." });
    } catch (error) {
      this.setState({ saving: false, error: error instanceof Error ? error.message : "Impossible d'enregistrer le formulaire." });
    }
  };

  /**
   * Met à jour la liste des champs et marque le formulaire comme modifié
   * @param {Array<DesignerFieldStateObj>} fields - Nouvelle liste des champs
   * @param {?string} [selectedKey] - Clé du champ à sélectionner (inchangée si non fournie)
   */
  setFields(fields, selectedKey) {
    this.setState({
      fields,
      selectedKey: selectedKey === undefined ? this.state.selectedKey : selectedKey,
      dirty: true,
      success: ""
    });
  }

  /**
   * Ajoute un nouveau champ texte en fin de formulaire et l'ouvre en édition
   */
  addField = () => {
    const field = withKey({
      id: null,
      label: "Nouveau champ",
      type: "string",
      list: null,
      placeholder: null,
      help: null,
      required: false,
      position: this.state.fields.length + 1,
      has_other_response: false,
      state: true,
      go_to_next_line: true
    });

    this.setFields([...this.state.fields, field], field.key);
  };

  /**
   * Retire un champ du formulaire (supprimé à l'enregistrement)
   * @param {DesignerFieldStateObj} field - Champ à retirer
   */
  removeField = field => {
    const message = field.id ? `Supprimer le champ « ${field.label} » ? Les réponses déjà reçues pour ce champ ne seront plus affichées dans les résultats.` : `Supprimer le champ « ${field.label} » ?`;

    if (!window.confirm(message)) {
      return;
    }

    this.setFields(
      this.state.fields.filter(item => item.key !== field.key),
      this.state.selectedKey === field.key ? null : this.state.selectedKey
    );
  };

  /**
   * Modifie une propriété d'un champ
   * @param {string} key - Clé du champ
   * @param {keyof DesignerFieldObj} property - Propriété à modifier
   * @param {any} value - Nouvelle valeur
   */
  changeField = (key, property, value) => {
    this.setFields(this.state.fields.map(field => (field.key === key ? { ...field, [property]: value } : field)));
  };

  /**
   * Sélectionne un champ pour l'éditer
   * @param {DesignerFieldStateObj} field - Champ à éditer
   */
  selectField = field => {
    this.setState({ selectedKey: field.key });
  };

  /**
   * Fin du glisser-déposer d'un champ : réordonne les champs
   * @param {import('@dnd-kit/core').DragEndEvent} event - Événement de fin de glisser-déposer
   */
  onDragEnd = event => {
    const { active, over } = event;
    const fromIndex = this.state.fields.findIndex(field => field.key === active.id);
    const toIndex = over ? this.state.fields.findIndex(field => field.key === over.id) : -1;

    if (toIndex !== -1 && fromIndex !== toIndex) {
      this.setFields(arrayMove(this.state.fields, fromIndex, toIndex));
    }
  };

  render() {
    const { title, fields, types, selectedKey, loading, saving, dirty, error, success } = this.state;
    const selectedField = fields.find(field => field.key === selectedKey) || null;

    if (loading) {
      return <p className="paragraph">Chargement du formulaire...</p>;
    }

    return (
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr", height: "100%", gap: "8px", minHeight: 0 }}>
        <div className="uiblock uiblock--no-header">
          <div className="uiblock__content">
            <div className="flexline flexline--gap-8" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <h4 className="title">Designer du formulaire « {title} »</h4>
                <div className="space--8"></div>
                <p className="paragraph paragraph--no-margin">Ajoutez des champs, réordonnez-les par glisser-déposer puis cliquez sur un champ pour modifier ses propriétés. Les modifications sont appliquées au formulaire à l'enregistrement.</p>
              </div>
              <div className="flexline flexline--gap-8">
                <button type="button" className="btn" onClick={this.addField} disabled={saving}>
                  <span className="material-icons">add</span>Ajouter un champ
                </button>
                <button type="button" className="btn btn--main-color" onClick={this.save} disabled={saving || !dirty}>
                  <span className="material-icons">save</span>
                  {saving ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>
            {dirty && (
              <>
                <div className="space--8"></div>
                <div className="badge">Modifications non enregistrées</div>
              </>
            )}
            {error && (
              <>
                <div className="space--8"></div>
                <div className="badge badge--danger">{error}</div>
              </>
            )}
            {success && (
              <>
                <div className="space--8"></div>
                <div className="badge badge--success">{success}</div>
              </>
            )}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)", gap: "8px", minHeight: 0 }}>
          <div style={{ minHeight: 0 }}>
            {!fields.length && <NoResult mode="light" title="Aucun champ" message="Ce formulaire ne contient aucun champ. Cliquez sur « Ajouter un champ » pour commencer." />}
            {fields.length > 0 && (
              <div className="table table--sticky" style={{ height: "100%", overflow: "auto", minHeight: 0 }}>
                <table className="table__table">
                  <thead>
                    <tr className="table__tr">
                      <th className="table__th table__th-sticky"></th>
                      <th className="table__th table__th-sticky">Position</th>
                      <th className="table__th table__th-sticky">Libellé</th>
                      <th className="table__th table__th-sticky">Type</th>
                      <th className="table__th table__th-sticky">Options</th>
                      <th className="table__th table__th-sticky"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <DndContext sensors={SENSORS} collisionDetection={closestCenter} onDragEnd={this.onDragEnd}>
                      <SortableContext items={fields.map(field => field.key)} strategy={verticalListSortingStrategy}>
                        {fields.map((field, index) => (
                          <FormDesignerField key={field.key} field={field} index={index} types={types} selected={field.key === selectedKey} disabled={saving} onSelect={this.selectField} onRemove={this.removeField} />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ minHeight: 0 }}>{selectedField ? <FormDesignerFieldEditor field={selectedField} types={types} disabled={saving} onChange={this.changeField} /> : <NoResult mode="light" title="Aucun champ sélectionné" message="Cliquez sur un champ pour modifier ses propriétés." />}</div>
        </div>
      </div>
    );
  }
}

const formDesignerNode = document.getElementById("form-designer");

if (formDesignerNode) {
  const root = ReactDOM.createRoot(formDesignerNode);

  root.render(<FormDesigner csrf={formDesignerNode.dataset.csrf} formId={formDesignerNode.dataset.formId} />);
}
